'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { CollapseRail } from '@/components/shared/collapse-rail';
import { ScrollArea } from '@/components/ui/scroll-area';
import { NavigationLoadingOverlay } from '@/components/ui/navigation-loading-overlay';
import { useNavigationLoading } from '@/lib/contexts/navigation-loading-context';
import { getScrollPositionActions } from '@/lib/stores';

interface LayoutWrapperProps {
  children: React.ReactNode;
}

/**
 * The single shared shell for every top-level page — Matters, Scribe, Intake,
 * Templates (LF-5279). It owns the page chrome in one place so it can't drift
 * between pages: the narrow {@link CollapseRail}, the custom Radix scrollbar
 * (one bounded {@link ScrollArea}), the white content region, and per-route
 * scroll-position persistence (restores scroll when navigating back from a
 * matter's details). Page content renders as `children`; the Matters
 * dashboard views add their matter-selection context + banner slot in
 * `app/(dashboard)/(views)/layout.tsx`.
 */
export function LayoutWrapper({ children }: LayoutWrapperProps) {
  const pathname = usePathname();
  const { navigationState } = useNavigationLoading();
  const scrollRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // The scrollable viewport lives inside the Radix ScrollArea.
  const getViewport = useCallback(() => {
    return scrollRef.current?.querySelector(
      '[data-radix-scroll-area-viewport]'
    ) as HTMLElement | null;
  }, []);

  // Save scroll position on scroll (debounced).
  useEffect(() => {
    const viewport = getViewport();
    if (!viewport || !pathname) return;

    const handleScroll = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        getScrollPositionActions().save(pathname, viewport.scrollTop);
      }, 100);
    };

    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      viewport.removeEventListener('scroll', handleScroll);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [pathname, getViewport]);

  // Restore the saved scroll position when the route changes.
  useEffect(() => {
    if (!pathname) return;
    const timer = setTimeout(() => {
      const viewport = getViewport();
      if (!viewport) return;
      const saved = getScrollPositionActions().get(pathname);
      if (saved > 0) viewport.scrollTop = saved;
    }, 50);
    return () => clearTimeout(timer);
  }, [pathname, getViewport]);

  return (
    <div className="print-body flex h-full flex-1 overflow-hidden">
      <CollapseRail />
      <div className="relative flex min-w-0 flex-1 flex-col bg-white">
        {/* One bounded scroll region for every top-level page — the shared
            custom Radix scrollbar. Height is bounded by the `h-screen`
            SidebarInset above, so content scrolls here rather than at the
            browser window. The `!block` override undoes Radix's `display:table`
            viewport wrapper so `min-w-0`/`max-w-*` content sizing works. */}
        <ScrollArea
          ref={scrollRef}
          className="h-full [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:h-full"
        >
          {/* `min-h-full`, not `min-h-screen` (VALE-345). Both fill the
              viewport for a short page — the ScrollArea viewport is exactly
              window height — but `min-h-screen` also makes this box impossible
              to BOUND, so nothing inside it can own its own scrolling and every
              page has to scroll as one block. With `min-h-full` a page can opt
              in by using `ScrollShell`/`ListPageShell` (`flex-1 min-h-0`) and
              keep its list's scrollbar on the list. Pages that don't opt in are
              unaffected: they grow past the viewport and scroll here exactly as
              before, keeping the shared custom scrollbar and the per-page
              scroll-position restore (LF-5279). */}
          <div className="flex h-full min-w-0 flex-col">{children}</div>
        </ScrollArea>
        <NavigationLoadingOverlay
          isLoading={navigationState.isNavigating}
          message={navigationState.destination}
        />
      </div>
    </div>
  );
}
